/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Sparkles, User, Bot, CheckCircle, Network, Zap, Brain, Share2, Minus, History, Loader, Paperclip } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import ShareDialog from './ShareDialog';
import ResponseSourceIndicator from './ResponseSourceIndicator';
import { AgentToggle, ActionCard, ConfirmationBar, EVMDashboard, TrustScore, VoiceInput, speakText, stopSpeaking } from './agent';

// Same shape as apps/core/src/services/api.js — resolve API base for direct
// fetch (multipart uploads can't go through api.request(), which forces
// Content-Type: application/json and breaks the multipart boundary).
const RAW_API_URL = process.env.REACT_APP_API_URL || 'https://skilldelivery-platform.onrender.com/api';
const API_URL = RAW_API_URL.endsWith('/api') ? RAW_API_URL : `${RAW_API_URL.replace(/\/$/, '')}/api`;

// Track configuration
const trackIcons = {
  WMS: '📦',
  iSeries: '💻',
  Civil: '🏗️',
  Stitching: '🧵',
  SkillCode: '🖥️',
  Travel: '✈'
};

const trackNames = {
  WMS: 'WMS',
  iSeries: 'iSeries',
  Civil: 'Civil Construction',
  Stitching: 'Stitching',
  SkillCode: 'SkillCode',
  Travel: 'यात्रा / Travel'
};

const SkillBotEnhanced = ({ fullPage = false }) => {
  const { user } = useAuth();
  // In fullPage mode, chat is always open and can't be minimized
  const [isOpen, setIsOpen] = useState(fullPage);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [contextMode, setContextMode] = useState('generic');
  const [userName, setUserName] = useState(null);
  const [trackContext, setTrackContext] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [userTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const messagesEndRef = useRef(null);
  
  // ✅ NEW: Loading state for history
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // Agent mode state
  const [isAgentMode, setIsAgentMode] = useState(() => localStorage.getItem('agent_mode') === 'true');
  const [agentDomains, setAgentDomains] = useState([]);
  const [agentEnabled, setAgentEnabled] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [agentState, setAgentState] = useState({ phase: 'idle', trustScore: 0 });
  const [awaitingApproval, setAwaitingApproval] = useState(false);
  const [speakingMsgIdx, setSpeakingMsgIdx] = useState(null);
  const [autoSpeak, setAutoSpeak] = useState(false);
  const [interimText, setInterimText] = useState('');
  const [wikiSuggestions, setWikiSuggestions] = useState({});
  const [scoutSuggestions, setScoutSuggestions] = useState({});
  // Voice/file transcription UI state — echo-confirm requires the user to
  // review before send. Never auto-send: a mis-heard warehouse instruction
  // must not execute unreviewed.
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcribeError, setTranscribeError] = useState(null);
  const audioFileInputRef = useRef(null);

  // Agent mode: Check if agent is available on backend
  useEffect(() => {
    const checkAgentAvailability = async () => {
      try {
        const res = await api.get('/agent/domains');
        const data = res?.data || res;
        if (data?.enabled && data?.domains?.length > 0) {
          setAgentEnabled(true);
          setAgentDomains(data.domains);
          // Auto-select domain if user's vertical matches an available domain
          const verticalMap = { WMS: 'wms', Civil: 'civil', iSeries: 'iseries', Stitching: 'stitching' };
          const userVertical = verticalMap[user?.vertical] || user?.vertical?.toLowerCase();
          if (userVertical) {
            const match = data.domains.find(d => d.domain === userVertical);
            if (match) setSelectedDomain(match.domain);
          }
        }
      } catch (err) {
        // Agent not available — silent
      }
    };
    checkAgentAvailability();
  }, []);

  // TTS: Load voices asynchronously (Chrome does this async)
  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis?.getVoices();
      if (voices?.length > 0) {
        console.log('[TTS] Voices loaded:', voices.length);
        const hindiVoices = voices.filter(v => v.lang.startsWith('hi'));
        if (hindiVoices.length > 0) {
          console.log('[TTS] Hindi voices:', hindiVoices.map(v => v.name));
        }
      }
    };
    loadVoices();
    window.speechSynthesis?.addEventListener('voiceschanged', loadVoices);
    return () => {
      window.speechSynthesis?.removeEventListener('voiceschanged', loadVoices);
      window.speechSynthesis?.cancel();
    };
  }, []);

  const toggleAgentMode = () => {
    const newMode = !isAgentMode;
    setIsAgentMode(newMode);
    localStorage.setItem('agent_mode', String(newMode));
    setAwaitingApproval(false);
    setAgentState({ phase: 'idle', trustScore: 0 });
    if (!newMode) setSelectedDomain(null);
  };

  // Agent mode: Get current domain vertical
  const getAgentVertical = () => {
    if (trackContext) {
      const map = { WMS: 'wms', Civil: 'civil', iSeries: 'iseries', Stitching: 'stitching', ERPNext: 'erpnext', Platform: 'platform' };
      //return map[trackContext] || trackContext?.toLowerCase();
      return map[trackContext] || (typeof trackContext === 'string' ? trackContext.toLowerCase() : 'wms');

    }
    //if (user?.vertical) return user.vertical.toLowerCase();
    if (user?.vertical) return (typeof user.vertical === 'string' ? user.vertical.toLowerCase() : String(user.vertical).toLowerCase());

    // Default to first available domain
    return agentDomains[0]?.domain || 'wms';
  };

  const getAgentDomainInfo = () => {
    if (selectedDomain) {
      return agentDomains.find(d => d.domain === selectedDomain) || agentDomains[0] || null;
    }
    const v = getAgentVertical();
    return agentDomains.find(d => d.domain === v) || agentDomains[0] || null;
  };

  // Agent mode: Send message to agent API
  const handleAgentSendMessage = async (messageText, action = null) => {
    if (!messageText?.trim() && !action) return;

    if (messageText?.trim()) {
      const userMessage = {
        type: 'user',
        content: messageText,
        timestamp: new Date(),
        timezone: userTimezone
      };
      setMessages(prev => [...prev, userMessage]);
      setInputMessage('');
    }

    setIsTyping(true);
    setAwaitingApproval(false);

    try {
      const res = await api.post('/agent/chat', {
        message: messageText || '',
        conversationId: conversationId || `agent_${Date.now()}`,
        vertical: selectedDomain || getAgentVertical(),
        action: action
      });

      const data = res?.data?.data || res?.data || res;

      if (data.type === 'blocked') {
        setMessages(prev => [...prev, {
          type: 'bot',
          content: data.message,
          timestamp: new Date(),
          isViolation: true
        }]);
      } else {
        const botMessage = {
          type: 'agent',
          content: data.message,
          timestamp: new Date(),
          actions: data.actions || null,
          evm: data.evm || null,
          awaitingApproval: data.awaitingApproval || false,
          agentState: data.state || null,
          domainIcon: data.domainIcon,
          ragSourceCount: data.ragSourceCount || 0,
          graphEntities: data.graphEntities || 0
        };

        setMessages(prev => [...prev, botMessage]);
        setAwaitingApproval(data.awaitingApproval || false);

        if (data.state) {
          setAgentState(data.state);
        }

        // Auto-speak agent response if enabled
        if (autoSpeak && data.message) {
          speakText(data.message);
        }
      }
    } catch (error) {
      console.error('Agent error:', error);
      const errorData = error.response?.data;
      setMessages(prev => [...prev, {
        type: 'bot',
        content: errorData?.message || "Agent service temporarily unavailable. Please try again.",
        timestamp: new Date()
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleAgentApprove = () => handleAgentSendMessage('', 'approve');
  const handleAgentReject = () => handleAgentSendMessage('', 'reject');

  // Echo-confirm: populate the input only, never auto-send. The user must
  // see what was transcribed and correct/approve before sending. WhatsApp
  // and Teams already echo "Voice note received:" before acting; the web
  // UI takes the same principle further by requiring an explicit send.
  const handleVoiceTranscript = (transcript) => {
    setInterimText('');
    setTranscribeError(null);
    if (transcript) setInputMessage(transcript);
  };

  // Upload an audio file to /api/transcribe and populate the input on success.
  const handleAudioFileUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    // Reset so the same file can be re-selected after an error
    if (audioFileInputRef.current) audioFileInputRef.current.value = '';
    if (!file) return;

    setTranscribeError(null);
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', file);
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      const res = await fetch(`${API_URL}/transcribe`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        // Surface both transport failures and guard-rails rejections
        const msg = data.message || data.error || `Transcription failed (${res.status})`;
        setTranscribeError(msg);
        return;
      }
      if (data.transcript) {
        setInputMessage(data.transcript);
      }
    } catch (err) {
      setTranscribeError((err && err.message) || 'Voice upload failed');
    } finally {
      setIsTranscribing(false);
    }
  };

  // Speaker button handler for individual messages
  const handleToggleSpeaker = (msgIdx, content) => {
    if (speakingMsgIdx === msgIdx) {
      stopSpeaking();
      setSpeakingMsgIdx(null);
    } else {
      const utterance = speakText(content);
      setSpeakingMsgIdx(msgIdx);
      if (utterance) {
        utterance.onend = () => setSpeakingMsgIdx(null);
        utterance.onerror = () => setSpeakingMsgIdx(null);
      }
    }
  };

  // ✅ NEW: Load conversation history when chat opens
  useEffect(() => {
    if (isOpen && user?.id && !historyLoaded) {
      loadConversationHistory();
    } else if (isOpen && !user && messages.length === 0) {
      // Guest user - just show welcome message
      setMessages([createWelcomeMessage()]);
    }
  }, [isOpen, user?.id]);

  // ✅ NEW: Function to load history from backend
  const loadConversationHistory = async () => {
    if (!user?.id) return;
    
    setIsLoadingHistory(true);
    try {
      console.log('📜 Loading conversation history for user:', user.id);
      
      const response = await api.getChatHistory(user.id, { limit: 50 });
      
      if (response.success && response.history) {
        const loadedMessages = transformHistoryToMessages(response.history);
        
        if (loadedMessages.length > 0) {
          console.log(`✅ Loaded ${loadedMessages.length} messages from history`);
          setMessages(loadedMessages);
        } else {
          // No history, show welcome message
          console.log('📝 No history found, showing welcome message');
          setMessages([createWelcomeMessage()]);
        }
      } else {
        setMessages([createWelcomeMessage()]);
      }
      
      setHistoryLoaded(true);
    } catch (error) {
      console.error('❌ Failed to load conversation history:', error);
      // Show welcome message on error
      setMessages([createWelcomeMessage()]);
      setHistoryLoaded(true);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // ✅ NEW: Transform backend history format to frontend message format
  const transformHistoryToMessages = (history) => {
    const messages = [];
    
    // History could be an array of sessions or a single session
    const sessions = Array.isArray(history) ? history : [history];
    
    for (const session of sessions) {
      if (!session.messages || !Array.isArray(session.messages)) continue;
      
      for (const msg of session.messages) {
        // Handle format: { userMessage, botResponse, timestamp, ... }
        if (msg.userMessage) {
          messages.push({
            type: 'user',
            content: msg.userMessage,
            timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
            timezone: userTimezone,
            usedKnowledgeBase: false,
            usedGraphRAG: false,
            sources: [],
            skillPath: [],
            relatedSkills: [],
            modelUsed: null,
            confidence: null
          });
        }
        
        if (msg.botResponse) {
          messages.push({
            type: 'bot',
            content: msg.botResponse,
            timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
            timezone: userTimezone,
            usedKnowledgeBase: msg.documentsUsed?.length > 0 || false,
            usedGraphRAG: msg.usedGraphRAG || false,
            sources: msg.documentsUsed || [],
            skillPath: msg.skillsDiscussed || [],
            relatedSkills: [],
            modelUsed: msg.modelUsed || null,
            confidence: msg.confidence || null
          });
        }
        
        // Handle format: { role: 'user'/'assistant', content, timestamp }
        if (msg.role && msg.content) {
          messages.push({
            type: msg.role === 'user' ? 'user' : 'bot',
            content: msg.content,
            timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
            timezone: userTimezone,
            usedKnowledgeBase: false,
            usedGraphRAG: false,
            sources: [],
            skillPath: [],
            relatedSkills: [],
            modelUsed: null,
            confidence: null
          });
        }
      }
    }
    
    // Sort by timestamp
    messages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    
    return messages;
  };

  // ✅ NEW: Create welcome message helper
  const createWelcomeMessage = () => ({
    type: 'bot',
    content: getWelcomeMessage(),
    timestamp: new Date(),
    usedKnowledgeBase: false,
    usedGraphRAG: false,
    sources: [],
    skillPath: [],
    relatedSkills: [],
    modelUsed: null,
    confidence: null
  });

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: userTimezone
    });
  };

  // ✅ NEW: Format date for date separators
  const formatDate = (date) => {
    const today = new Date();
    const msgDate = new Date(date);
    
    if (msgDate.toDateString() === today.toDateString()) {
      return 'Today';
    }
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (msgDate.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    
    return msgDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });
  };

  // ✅ NEW: Check if we should show date separator
  const shouldShowDateSeparator = (currentMsg, prevMsg) => {
    if (!prevMsg) return true;
    
    const currentDate = new Date(currentMsg.timestamp).toDateString();
    const prevDate = new Date(prevMsg.timestamp).toDateString();
    
    return currentDate !== prevDate;
  };

  const getWelcomeMessage = () => {
    const displayName = userName || user?.fullName || user?.name || 'there';
    
    if (!user) {
      return "Hi, Welcome to SkillDelivery! I'm SkillBot 👋 I can help you explore career paths in WMS, iSeries, Civil Construction, or Stitching. What would you like to know?";
    }
    
    return `Hi ${displayName}, Welcome to SkillDelivery! 👋 I'm SkillBot, your AI career advisor. I'm here to help you with your ${user.vertical || ''} career journey. What would you like to know?`;
  };

  const getContextBadge = () => {
    const badges = {
      generic: { label: 'Exploring', color: 'bg-gray-100 text-gray-700', icon: '🌍' },
      profile: { label: 'Profile Mode', color: 'bg-blue-100 text-blue-700', icon: '👤' },
      personalized: { label: 'Personalized', color: 'bg-green-100 text-green-700', icon: '✨' }
    };
    return badges[contextMode] || badges.generic;
  };

  const getModelBadge = (modelUsed, confidence) => {
    if (!modelUsed) return null;
    
    if (modelUsed === 'gemma2:9b') {
      return {
        label: 'Gemma 2',
        icon: Zap,
        color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        tooltip: `Fast local model (${confidence}% confidence)`
      };
    } else if (modelUsed === 'claude-3.5-sonnet' || modelUsed.includes('claude')) {
      return {
        label: 'Claude',
        icon: Brain,
        color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
        tooltip: `Premium AI model (${confidence}% confidence)`
      };
    } else if (modelUsed.includes('mistral')) {
      return {
        label: 'Mistral',
        icon: Zap,
        color: 'bg-orange-50 text-orange-700 border-orange-200',
        tooltip: `Fine-tuned domain model (${confidence}% confidence)`
      };
    }
    return null;
  };

  const fetchWikiSuggestions = async (messageId, query) => {
    try {
      const domain = user?.vertical?.toLowerCase() || 'wms';
      const res = await api.post('/wiki/search/relevant', {
        text: query.slice(0, 200),
        domain,
        limit: 3
      });
      const entries = res.data?.entries || res.entries || [];
      if (entries.length > 0) {
        setWikiSuggestions(prev => ({ ...prev, [messageId]: entries }));
      }
    } catch (e) {
      // fail silently
    }
  };

  const fetchScoutSuggestions = async (messageId, query) => {
    try {
      const domain = user?.vertical?.toLowerCase() || 'wms';
      const text = query.toLowerCase();

      const categories = [];
      if (/learn|train|certif|course|study|skill/.test(text)) {
        categories.push('courses');
        categories.push('youtube');
      }
      if (/job|career|hire|salary|work|role|position/.test(text)) {
        categories.push('job_boards');
      }
      if (/community|forum|network|group|connect/.test(text)) {
        categories.push('communities');
      }
      if (/news|update|latest|trend|2026|new/.test(text)) {
        categories.push('scout');
      }
      if (categories.length === 0) {
        categories.push('youtube');
      }

      const results = [];
      for (const cat of categories.slice(0, 2)) {
        try {
          const res = await api.get(
            `/wiki/feed/learning_hub?domain=${domain}&category=${cat}&limit=1`
          );
          const entries = res.data?.entries || res.entries || [];
          if (entries.length > 0) {
            results.push({ ...entries[0], category: cat });
          }
        } catch (e) {}
      }

      if (results.length > 0) {
        setScoutSuggestions(prev => ({
          ...prev,
          [messageId]: results
        }));
      }
    } catch (e) {}
  };

  const fetchCuratedResources = async (
    question, domain, msgKey, entityType) => {
    try {
      const res = await api.post(
        '/wiki/resolve/resources',
        {
          question,
          domain: domain?.toLowerCase() || 'wms',
          entityType: entityType || 'individual'
        }
      );
      // Handle both res.data and res directly
      const rawData = res?.data ?? res;

      console.log('[Curated] Raw data type:',
        typeof rawData,
        'keys:', Object.keys(rawData || {})
          .join(',').substring(0, 100));

      const resources = rawData?.resources
        || rawData?.data?.resources
        || (Array.isArray(rawData) ? rawData : []);

      console.log('[Curated] Resources found:',
        resources.length);

      if (resources.length > 0) {
        const pickUrl = (r) =>
          r.url || r.source_url || r.source_metadata?.source_url;
        const pickTitle = (r) => r.title || r.topic || r.name;
        const mapped = resources
          .filter(pickUrl)
          .map(r => {
            const url = pickUrl(r);
            return {
              id: r.id || url,
              topic: pickTitle(r),
              summary: r.summary || '',
              source_metadata: { source_url: url },
              category: ({
                video: 'youtube',
                course: 'courses',
                job: 'job_boards',
                community: 'communities'
              })[r.entry_type || r.type],
              source: 'wiki_curated',
              quality_score: r.quality_score || 0.75
            };
          });

        console.log('[Curated] Mapped items:',
          mapped.length, mapped.map(r => r.topic));

        if (mapped.length > 0) {
          setScoutSuggestions(prev => ({
            ...prev,
            [msgKey]: [
              ...(prev[msgKey] || []),
              ...mapped
            ]
          }));
          console.log('[Curated] Scout state updated for key:', msgKey);
        }
      }
    } catch(e) {
      console.warn('[Curated] Failed:', e.message);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    // Route to agent API when agent mode is ON and domain is selected
    if (isAgentMode && agentEnabled) {
      if (!selectedDomain) return;
      return handleAgentSendMessage(inputMessage);
    }

    const userMessage = {
      type: 'user',
      content: inputMessage,
      timestamp: new Date(),
      timezone: userTimezone,
      usedKnowledgeBase: false,
      usedGraphRAG: false,
      sources: [],
      skillPath: [],
      relatedSkills: [],
      modelUsed: null,
      confidence: null
    };

    setMessages(prev => [...prev, userMessage]);
    const savedInput = inputMessage;
    setInputMessage('');
    setIsTyping(true);

    // ── SkillCode redirect (check FIRST to avoid false wiki matches) ──
    const skillcodePatterns = [
      /which files/i, /file.*depend/i, /import.*file/i,
      /show.*procedure/i, /most dependent/i, /safe to modify/i,
      /how many files/i, /codebase/i, /repository/i,
      /\.js\b/, /\.py\b/, /function.*import/i
    ];
    const isSkillCodeQuery = skillcodePatterns.some(p => p.test(savedInput));
    const userVertical = user?.vertical?.toLowerCase();
    if (userVertical === 'skillcode' || isSkillCodeQuery) {
      const scId = 'sc_' + Date.now().toString();
      const skillCodeMessage = {
        type: 'bot',
        id: scId,
        messageId: scId,
        timestamp: new Date(),
        content: `SkillCode questions are best handled in the SkillCode tool which has direct access to your codebase and repository intelligence.\n\n**[Open SkillCode →](/skillcode)**\n\nOr I can try to answer general coding questions here if you prefer.`,
        isSkillCodeRedirect: true,
        sources: []
      };
      setMessages(prev => [...prev, skillCodeMessage]);
      setInputMessage('');
      setIsTyping(false);
      return;
    }
    // ── End SkillCode redirect ──

    // ── Wiki Resolve ──
    let wikiResolved = false;
    try {
      const domain = user?.vertical?.toLowerCase() || 'wms';
      const resolveRes = await api.post('/wiki/resolve', {
        question: savedInput,
        domain,
        userId: user?.id,
        entityType: user?.entityType || user?.entity_type || 'individual',
        goals: user?.goals || []
      });
      const resolveData = resolveRes.data || resolveRes;

      if (resolveData.resolved && resolveData.entry) {
        wikiResolved = true;
        const entry = resolveData.entry;
        const msgKey = 'wiki_' + Date.now().toString();

        const wikiMessage = {
          type: 'bot',
          id: msgKey,
          messageId: msgKey,
          content: entry.content || entry.summary,
          sources: [],
          isFromWiki: true,
          wikiTopic: entry.topic,
          wikiScore: entry.quality_score || 0,
          timestamp: new Date(),
          time: new Date().toLocaleTimeString('en-IN', {
            hour: '2-digit', minute: '2-digit', hour12: true
          })
        };

        setMessages(prev => [...prev, wikiMessage]);
        setIsTyping(false);

        fetchScoutSuggestions(msgKey, savedInput);
        fetchCuratedResources(
          savedInput,
          domain,
          msgKey,
          user?.entityType || user?.entity_type || 'individual'
        );
        return;
      }
    } catch (e) {
      // Wiki resolve failed — continue to adapter
    }

    if (wikiResolved) return;
    // ── End Wiki Resolve ──

    try {
      const response = await api.sendChatMessage(user?.id, savedInput, userTimezone);
      
      if (response.success) {
        setContextMode(response.data.contextMode);

        if (response.data.userName && !userName) {
          setUserName(response.data.userName);
        }

        if (response.data.trackContext) {
          setTrackContext(response.data.trackContext);
        }

        if (response.data.conversationId) {
          setConversationId(response.data.conversationId);
        }

        const botMessage = {
          type: 'bot',
          content: response.data.message,
          timestamp: new Date(),
          timezone: userTimezone,
          usedKnowledgeBase: response.data.usedKnowledgeBase || false,
          usedGraphRAG: response.data.usedGraphRAG || false,
          sources: response.data.sources || [],
          skillPath: response.data.skillPath || [],
          relatedSkills: response.data.relatedSkills || [],
          extractedSkills: response.data.extractedSkills || [],
          modelUsed: response.data.modelUsed || null,
          confidence: response.data.confidence || null,
          // Phase 2B: Response source transparency
          messageId: response.data.messageId || null,
          sourceInfo: response.data.sourceInfo || null,
          transparency: response.data.transparency || null
        };

        setMessages(prev => [...prev, botMessage]);

        // Determine message key
        const msgKey = botMessage.id
          || botMessage.messageId
          || Date.now().toString();

        // Fetch wiki + scout suggestions in parallel
        fetchWikiSuggestions(msgKey, savedInput);
        fetchScoutSuggestions(msgKey, savedInput);
        fetchCuratedResources(
          savedInput,
          user?.vertical?.toLowerCase() || 'wms',
          msgKey,
          user?.entityType || user?.entity_type || 'individual'
        );

        // Observe answer for SEAL/wiki quality storage
        try {
          const domain = user?.vertical?.toLowerCase() || 'wms';
          api.post('/wiki/agent/observe', {
            type: 'skillbot_answer',
            domain,
            source: 'skillbot',
            payload: {
              question: savedInput,
              answer: response.data.message || botMessage.content || '',
              domain,
              sources: botMessage.sources || [],
            }
          }).catch(() => {});
        } catch (e) {}
      }
    } catch (error) {
      console.error('Chat error:', error);

      // Check if this is a content policy violation with detailed info
      const errorData = error.response?.data;
      let errorContent = "Sorry, I'm having trouble right now. Please try again.";

      if (errorData?.blocked === true && errorData?.violation) {
        // Build detailed violation message for the user
        const v = errorData.violation;
        const s = errorData.strikes;
        const w = errorData.warning;

        let parts = [];
        parts.push('**' + (v.title || 'Content Policy Violation') + '**');
        parts.push('');
        parts.push(v.description || v.userMessage || errorData.message || '');
        parts.push('');
        parts.push('**What to do:** ' + (v.userMessage || 'Please rephrase your message.'));

        if (s && typeof s.total === 'number') {
          parts.push('');
          parts.push('---');
          parts.push('**Strike ' + s.total + ' of ' + s.maxAllowed + '** \u2014 ' + (s.remaining > 0 ? s.remaining + ' remaining before suspension' : 'Account suspended'));
        }

        if (w?.message) {
          parts.push('');
          parts.push('> ' + w.message);
        }

        if (errorData.support?.email) {
          parts.push('');
          parts.push('*Questions? Contact ' + errorData.support.email + '*');
        }

        errorContent = parts.join('\n');
      } else if (errorData?.error === 'CONTENT_POLICY_VIOLATION') {
        // Simpler violation response (fallback)
        errorContent = errorData.message || 'Your message was blocked due to a content policy violation.';
      } else if (errorData?.error === 'ACCOUNT_SUSPENDED') {
        errorContent = '**Account Suspended**\n\nYour account has been suspended due to repeated policy violations. Please contact support@skilldelivery.co.in to appeal.';
      }

      setMessages(prev => [...prev, {
        type: 'bot',
        content: errorContent,
        timestamp: new Date(),
        timezone: userTimezone,
        usedKnowledgeBase: false,
        usedGraphRAG: false,
        sources: [],
        skillPath: [],
        relatedSkills: [],
        modelUsed: null,
        confidence: null,
        isViolation: errorData?.blocked === true
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ✅ NEW: Clear chat and start fresh
  const handleClearChat = () => {
    setMessages([createWelcomeMessage()]);
    setHistoryLoaded(true); // Mark as loaded to prevent re-fetching
  };

  // ✅ NEW: Refresh history from server
  const handleRefreshHistory = () => {
    setHistoryLoaded(false);
    loadConversationHistory();
  };

  const suggestedQuestions = [
    "What should I learn next?",
    "Tell me about my career path",
    "How can I improve my skills?",
    "What jobs am I qualified for?"
  ];

  // In fullPage mode, skip floating button and minimized bar
  if (!fullPage) {
    // Floating button when completely closed
    if (!isOpen && !isMinimized) {
      return (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group z-50"
        >
          <MessageCircle className="w-6 h-6 text-white" />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></span>
        </button>
      );
    }

    // Minimized bar
    if (isMinimized) {
      return (
        <div
          onClick={() => setIsMinimized(false)}
          className="fixed bottom-6 right-6 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-4 py-3 rounded-full shadow-lg cursor-pointer flex items-center gap-3 z-50 hover:shadow-xl transition-all"
        >
          <Sparkles className="w-5 h-5" />
          <span className="font-medium">SkillBot</span>
          <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
            {messages.length} messages
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
              setIsMinimized(false);
            }}
            className="ml-2 hover:bg-white/20 rounded-full p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      );
    }
  }

  // Main chat window
  // Container classes differ based on fullPage mode
  const containerClasses = fullPage
    ? "flex flex-col h-full bg-white overflow-hidden"
    : "fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden border border-gray-200";

  return (
    <div className={containerClasses}>
      {/* Mic pulse animation for voice input */}
      <style>{`
        @keyframes micPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
          50% { box-shadow: 0 0 0 12px rgba(239, 68, 68, 0); }
        }
      `}</style>

      {/* Header */}
      <div className={`bg-gradient-to-r from-cyan-500 to-blue-500 text-white ${fullPage ? 'p-5' : 'p-4'} flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <div className={`${fullPage ? 'w-12 h-12' : 'w-10 h-10'} bg-white/20 rounded-full flex items-center justify-center`}>
            <Sparkles className={fullPage ? "w-6 h-6" : "w-5 h-5"} />
          </div>
          <div>
            <h3 className={`font-semibold ${fullPage ? 'text-xl' : ''}`}>
              {isAgentMode && agentEnabled ? (getAgentDomainInfo()?.displayName || 'Agent') : 'SkillBot'}
            </h3>
            <p className={`${fullPage ? 'text-sm' : 'text-xs'} text-white/80`}>
              {isAgentMode && agentEnabled ? (
                <span>{getAgentDomainInfo()?.icon || ''} {agentState.phase !== 'idle' ? agentState.phase.replace('_', ' ') : 'Ready'}</span>
              ) : trackContext ? (
                <span>{trackIcons[trackContext]} {trackNames[trackContext]} Expert</span>
              ) : (
                <span>AI Career Advisor</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Agent mode toggle */}
          {agentEnabled && (
            <AgentToggle
              isAgentMode={isAgentMode}
              onToggle={toggleAgentMode}
              domainName={getAgentDomainInfo()?.displayName}
              domainIcon={getAgentDomainInfo()?.icon}
              fullPage={fullPage}
            />
          )}
          {/* Trust score in agent mode */}
          {isAgentMode && agentEnabled && agentState.trustScore > 0 && (
            <TrustScore score={agentState.trustScore} />
          )}
          {/* Refresh history button */}
          {user && (
            <button
              onClick={handleRefreshHistory}
              className={`${fullPage ? 'p-2.5' : 'p-2'} hover:bg-white/20 rounded-lg transition-colors`}
              title="Refresh conversation history"
              disabled={isLoadingHistory}
            >
              <History className={`${fullPage ? 'w-5 h-5' : 'w-5 h-5'} ${isLoadingHistory ? 'animate-spin' : ''}`} />
            </button>
          )}
          <button
            onClick={() => setShowShareDialog(true)}
            className={`${fullPage ? 'p-2.5' : 'p-2'} hover:bg-white/20 rounded-lg transition-colors`}
            title="Share conversation"
          >
            <Share2 className={fullPage ? "w-5 h-5" : "w-5 h-5"} />
          </button>
          {/* Only show minimize/close in floating mode */}
          {!fullPage && (
            <>
              <button
                onClick={() => setIsMinimized(true)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Minimize"
              >
                <Minus className="w-5 h-5" />
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setIsMinimized(false);
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Context Badge - EXACT ORIGINAL */}
      <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getContextBadge().color}`}>
          <span>{getContextBadge().icon}</span>
          {getContextBadge().label}
        </span>
        {/* ✅ NEW: History loaded indicator */}
        {historyLoaded && messages.length > 1 && (
          <span className="text-xs text-gray-400">
            {messages.length} messages
          </span>
        )}
      </div>

      {/* Agent Domain Selector — shows when agent mode ON */}
      {isAgentMode && agentEnabled && (
        <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-2 bg-white">
          <span className="text-xs text-gray-500 font-medium">Domain:</span>
          {agentDomains.map(d => (
            <button
              key={d.domain}
              onClick={() => setSelectedDomain(d.domain)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                selectedDomain === d.domain
                  ? 'bg-cyan-100 text-cyan-700 border-2 border-cyan-400'
                  : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
              }`}
            >
              {d.icon} {d.displayName}
            </button>
          ))}
          {!selectedDomain && (
            <span className="text-xs text-amber-600 ml-1">Select a domain to start</span>
          )}
          {/* Auto-speak toggle */}
          {selectedDomain && (
            <label className="ml-auto flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={autoSpeak}
                onChange={(e) => setAutoSpeak(e.target.checked)}
                className="accent-cyan-500"
              />
              <span>Auto-speak</span>
            </label>
          )}
        </div>
      )}

      {/* ✅ NEW: Loading History Indicator */}
      {isLoadingHistory && (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <Loader className="w-8 h-8 text-cyan-500 animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-600">Loading conversation history...</p>
          </div>
        </div>
      )}

      {/* Messages - EXACT ORIGINAL + timestamps + date separators */}
      {!isLoadingHistory && (
        <div className={`flex-1 overflow-y-auto ${fullPage ? 'p-6' : 'p-4'} space-y-4 bg-gray-50`}>
          {messages.map((msg, idx) => (
            <React.Fragment key={idx}>
              {/* ✅ NEW: Date separator */}
              {shouldShowDateSeparator(msg, messages[idx - 1]) && (
                <div className="flex items-center justify-center my-4">
                  <div className="bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full">
                    {formatDate(msg.timestamp)}
                  </div>
                </div>
              )}
              
              <div className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex gap-2 ${fullPage ? 'max-w-[70%]' : 'max-w-[85%]'} ${msg.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                  <div className={`${fullPage ? 'w-10 h-10' : 'w-8 h-8'} rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.type === 'user'
                      ? 'bg-gradient-to-br from-cyan-400 to-blue-500'
                      : msg.type === 'agent'
                        ? 'bg-gradient-to-br from-amber-400 to-orange-500'
                        : 'bg-gradient-to-br from-purple-400 to-pink-500'
                  }`}>
                    {msg.type === 'user' ? (
                      <User className={`${fullPage ? 'w-5 h-5' : 'w-4 h-4'} text-white`} />
                    ) : (
                      <Bot className={`${fullPage ? 'w-5 h-5' : 'w-4 h-4'} text-white`} />
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className={`rounded-2xl ${fullPage ? 'px-5 py-3' : 'px-4 py-2'} ${
                      msg.type === 'user'
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                        : msg.type === 'agent'
                          ? 'bg-white text-gray-800 shadow-sm border border-amber-200'
                          : 'bg-white text-gray-800 shadow-sm border border-gray-100'
                    }`}>
                      <p className={`${fullPage ? 'text-base' : 'text-sm'} whitespace-pre-wrap leading-relaxed`}>{msg.content}</p>
                      {/* Timestamp */}
                      {msg.timestamp && (
                        <p className={`text-xs mt-1 ${
                          msg.type === 'user' ? 'text-white/70' : 'text-gray-400'
                        }`}>
                          {formatTime(msg.timestamp)}
                        </p>
                      )}
                    </div>
                    
                    {/* Model Badge + Response Source Indicator */}
                    {msg.type === 'bot' && (
                      <div className="flex items-center gap-2">
                        {/* Phase 2B: Response Source Indicator */}
                        {msg.messageId && msg.transparency && (
                          <ResponseSourceIndicator
                            messageId={msg.messageId}
                            transparency={msg.transparency}
                          />
                        )}

                        {/* Model Badge - EXACT ORIGINAL */}
                        {msg.modelUsed && (() => {
                          const badge = getModelBadge(msg.modelUsed, msg.confidence);
                          if (!badge) return null;
                          const IconComponent = badge.icon;
                          return (
                            <div
                              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${badge.color}`}
                              title={badge.tooltip}
                            >
                              <IconComponent className="w-3 h-3" />
                              <span>Powered by {badge.label}</span>
                              {msg.confidence && (
                                <span className="opacity-75">• {msg.confidence}%</span>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {/* GraphRAG Skill Path - EXACT ORIGINAL */}
                    {msg.type === 'bot' && msg.skillPath && msg.skillPath.length > 0 && (
                      <div className="bg-purple-50 rounded-lg px-3 py-2 border border-purple-200">
                        <p className="text-xs font-semibold text-purple-700 mb-2 flex items-center gap-1">
                          <Network className="w-3 h-3" />
                          🛤️ Learning Path:
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          {msg.skillPath.map((skill, sIdx) => (
                            <React.Fragment key={sIdx}>
                              <div className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                {skill}
                              </div>
                              {sIdx < msg.skillPath.length - 1 && (
                                <span className="text-purple-400 font-bold">→</span>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* GraphRAG Indicator - EXACT ORIGINAL */}
                    {msg.type === 'bot' && msg.usedGraphRAG && (
                      <div className="flex items-center gap-2 px-2">
                        <span className="w-2 h-2 bg-purple-600 rounded-full animate-pulse"></span>
                        <span className="text-xs text-purple-600 font-medium flex items-center gap-1">
                          <Network className="w-3 h-3" />
                          Enhanced with Knowledge Graph
                        </span>
                      </div>
                    )}

                    {/* Knowledge Base Indicator - EXACT ORIGINAL */}
                    {msg.type === 'bot' && msg.usedKnowledgeBase && !msg.usedGraphRAG && (
                      <div className="flex items-center gap-2 px-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-xs text-green-600 font-medium">
                          Answer from your knowledge base
                        </span>
                      </div>
                    )}

                    {/* Sources - EXACT ORIGINAL */}
                    {msg.type === 'bot' && msg.sources && msg.sources.length > 0 && (
                      <div className="bg-blue-50 rounded-lg px-3 py-2 border border-blue-100">
                        <p className="text-xs font-semibold text-blue-700 mb-1">📚 Sources:</p>
                        <div className="space-y-1">
                          {msg.sources.map((source, sIdx) => (
                            <div key={sIdx} className="text-xs text-blue-600 flex items-start gap-2">
                              <span className="font-bold">{sIdx + 1}.</span>
                              <div>
                                <span className="font-medium">{source.title || source.name || 'Document'}</span>
                                {source.vertical && source.category && (
                                  <span className="text-blue-500"> ({source.vertical} - {source.category})</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Wiki answer source badge */}
                    {msg.isFromWiki && (
                      <div style={{
                        marginTop: '8px',
                        padding: '6px 12px',
                        background: '#f0fdf4',
                        borderRadius: '8px',
                        fontSize: '12px',
                        color: '#166534',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        border: '1px solid #bbf7d0'
                      }}>
                        📚 Answered from Wiki · {msg.wikiTopic}
                        <span style={{color:'#94a3b8'}}>
                          {Math.round((msg.wikiScore||0)*100)}% confidence
                        </span>
                      </div>
                    )}

                    {/* Wiki suggestions (for adapter answers) */}
                    {msg.type === 'bot' && !msg.isFromWiki && !msg.isSkillCodeRedirect && (() => {
                      const msgKey = msg.id || msg.messageId;
                      const suggestions = wikiSuggestions[msgKey];
                      if (!suggestions?.length) return null;
                      return (
                        <div style={{
                          marginTop: '8px',
                          padding: '10px 12px',
                          background: '#faf5ff',
                          borderRadius: '8px',
                          border: '1px solid #e9d5ff'
                        }}>
                          <div style={{
                            fontSize: '12px',
                            color: '#7c3aed',
                            fontWeight: '600',
                            marginBottom: '6px'
                          }}>
                            📚 Related in your Wiki:
                          </div>
                          {suggestions.map(entry => (
                            <a key={entry.id}
                              href="/knowledge-base"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '5px 8px',
                                background: 'white',
                                borderRadius: '6px',
                                marginBottom: '4px',
                                textDecoration: 'none',
                                fontSize: '13px',
                                color: '#6d28d9',
                                border: '1px solid #ede9fe'
                              }}>
                              <span>📄</span>
                              <span style={{flex:1}}>{entry.topic}</span>
                              <span style={{fontSize:'11px',color:'#94a3b8'}}>
                                {Math.round((entry.quality_score||0.5)*100)}%
                              </span>
                            </a>
                          ))}
                        </div>
                      );
                    })()}

                    {/* Scout suggestions */}
                    {msg.type === 'bot' && !msg.isSkillCodeRedirect && (() => {
                      const msgKey = msg.id || msg.messageId;
                      const scout = scoutSuggestions[msgKey];
                      if (!scout?.length) return null;
                      return (
                        <div style={{
                          marginTop: '8px',
                          padding: '10px 12px',
                          background: '#fff7ed',
                          borderRadius: '8px',
                          border: '1px solid #fed7aa'
                        }}>
                          <div style={{
                            fontSize: '12px',
                            color: '#c2410c',
                            fontWeight: '600',
                            marginBottom: '6px'
                          }}>
                            🔍 Scout found for you:
                          </div>
                          {scout.map(entry => {
                            const icon =
                              entry.category === 'youtube' ? '📺' :
                              entry.category === 'job_boards' ? '💼' :
                              entry.category === 'courses' ? '🎓' :
                              entry.category === 'communities' ? '👥' : '📰';
                            const label =
                              entry.category === 'youtube' ? 'Watch' :
                              entry.category === 'job_boards' ? 'View Job' :
                              entry.category === 'courses' ? 'View Course' :
                              entry.category === 'communities' ? 'Join' : 'Read';
                            return (
                              <div key={entry.id} style={{
                                background: 'white',
                                borderRadius: '8px',
                                padding: '8px 10px',
                                marginBottom: '6px',
                                border: '1px solid #fed7aa'
                              }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: '6px'
                                }}>
                                  <span style={{fontSize:'16px'}}>{icon}</span>
                                  <div style={{flex:1}}>
                                    <div style={{
                                      fontSize: '13px',
                                      fontWeight: '500',
                                      color: '#1e293b',
                                      marginBottom: '2px'
                                    }}>
                                      {entry.topic}
                                    </div>
                                    <div style={{
                                      fontSize: '12px',
                                      color: '#64748b',
                                      marginBottom: '6px'
                                    }}>
                                      {(entry.summary||'').substring(0,80)}...
                                    </div>
                                    <a
                                      href={entry.source_metadata?.source_url
                                        || '/learning'}
                                      target={entry.source_metadata?.source_url
                                        ? '_blank' : '_self'}
                                      rel="noopener noreferrer"
                                      style={{
                                        fontSize: '12px',
                                        color: '#ea580c',
                                        textDecoration: 'none',
                                        fontWeight: '500'
                                      }}>
                                      {label} →
                                    </a>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {/* Agent: EVM Dashboard */}
                    {msg.type === 'agent' && msg.evm && (
                      <EVMDashboard evm={msg.evm} />
                    )}

                    {/* Agent: Action Cards */}
                    {msg.type === 'agent' && msg.actions && msg.actions.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-gray-500 uppercase">Proposed Actions:</p>
                        {msg.actions.map((action, aIdx) => (
                          <ActionCard key={aIdx} action={action} index={aIdx} />
                        ))}
                      </div>
                    )}

                    {/* Agent: RAG/Graph source counts + speaker button */}
                    {msg.type === 'agent' && (
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        {msg.ragSourceCount > 0 && <span>📚 {msg.ragSourceCount} sources</span>}
                        {msg.graphEntities > 0 && <span>🔗 {msg.graphEntities} graph entities</span>}
                        <button
                          onClick={() => handleToggleSpeaker(idx, msg.content)}
                          className={`ml-auto px-2 py-0.5 rounded text-xs transition-colors ${
                            speakingMsgIdx === idx
                              ? 'text-red-500 hover:text-red-600'
                              : 'text-gray-400 hover:text-cyan-600'
                          }`}
                          title={speakingMsgIdx === idx ? 'Stop speaking' : 'Listen to response'}
                        >
                          {speakingMsgIdx === idx ? '⏹ Stop' : '🔊 Sunein'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </React.Fragment>
          ))}

          {/* Typing Indicator - EXACT ORIGINAL */}
          {isTyping && (
            <div className="flex justify-start">
              <div className="flex gap-2 items-center">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-100">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Agent: Confirmation bar when awaiting approval */}
          {isAgentMode && awaitingApproval && !isTyping && (
            <ConfirmationBar
              onApprove={handleAgentApprove}
              onReject={handleAgentReject}
              disabled={isTyping}
            />
          )}

          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Suggested Questions - EXACT ORIGINAL */}
      {!isLoadingHistory && messages.length === 1 && (
        <div className="px-4 py-2 border-t border-gray-100 bg-white">
          <p className="text-xs text-gray-600 mb-2">Try asking:</p>
          <div className="flex flex-wrap gap-2">
            {suggestedQuestions.map((q, idx) => (
              <button
                key={idx}
                onClick={() => setInputMessage(q)}
                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-xs text-gray-700 transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Interim voice transcription preview */}
      {interimText && (
        <div className="px-4 py-2 bg-cyan-50 border-t border-cyan-200 text-sm text-cyan-700 italic">
          🎤 {interimText}...
        </div>
      )}

      {/* Input */}
      <div className={`${fullPage ? 'p-5' : 'p-4'} border-t border-gray-200 bg-white ${fullPage ? '' : 'rounded-b-2xl'}`}>
        <div className={`flex ${fullPage ? 'gap-3' : 'gap-2'}`}>
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isTranscribing ? 'Transcribing…' : (isAgentMode && agentEnabled ? (selectedDomain ? "Ask your agent... (Hindi/English)" : "Select a domain above to start...") : "Ask me anything...")}
            className={`flex-1 ${fullPage ? 'px-5 py-3 text-base' : 'px-4 py-2 text-sm'} border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none`}
            disabled={isTyping || isLoadingHistory || isTranscribing}
          />

          {/* Hidden file input for audio attach */}
          <input
            type="file"
            ref={audioFileInputRef}
            onChange={handleAudioFileUpload}
            accept="audio/m4a,audio/x-m4a,audio/mp4,audio/mpeg,audio/mp3,audio/ogg,audio/wav,audio/x-wav,audio/webm,.m4a,.mp3,.ogg,.wav,.webm"
            className="hidden"
            aria-hidden="true"
          />
          {/* Audio file attach button — same /api/transcribe route as the mic */}
          <button
            type="button"
            onClick={() => audioFileInputRef.current && audioFileInputRef.current.click()}
            disabled={isTyping || isLoadingHistory || isTranscribing}
            title="Attach an audio file (m4a, mp3, ogg, wav, webm — max 25 MB)"
            className="p-2 rounded-lg transition-all flex-shrink-0 bg-gray-100 hover:bg-gray-200 text-gray-600 disabled:opacity-50"
          >
            <Paperclip className="w-5 h-5" />
          </button>

          {/* Voice input — always visible; server transcription (Sarvam → Whisper) */}
          <VoiceInput
            mode="server"
            onTranscript={handleVoiceTranscript}
            onInterimText={setInterimText}
            disabled={isTyping || isLoadingHistory || isTranscribing}
            lang={(getAgentDomainInfo && getAgentDomainInfo()?.voiceLang?.[0]) || 'hi-IN'}
          />

          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isTyping || isLoadingHistory || isTranscribing || (isAgentMode && agentEnabled && !selectedDomain)}
            className={`${fullPage ? 'px-5 py-3' : 'px-4 py-2'} bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Send className={fullPage ? "w-6 h-6" : "w-5 h-5"} />
          </button>
        </div>

        {/* Visible transcribing state */}
        {isTranscribing && (
          <div className="mt-2 text-xs text-cyan-700 flex items-center gap-2">
            <Loader className="w-3 h-3 animate-spin" />
            Transcribing… you'll be able to review and edit before sending.
          </div>
        )}

        {/* Surface transcription / guard-rails errors */}
        {transcribeError && !isTranscribing && (
          <div className="mt-2 text-xs text-red-600 flex items-start justify-between gap-2 border border-red-200 bg-red-50 rounded-md px-2 py-1">
            <span className="flex-1">{transcribeError}</span>
            <button
              type="button"
              onClick={() => setTranscribeError(null)}
              className="text-red-400 hover:text-red-700 leading-none"
              aria-label="Dismiss"
            >×</button>
          </div>
        )}
      </div>

      {/* Share Dialog */}
      <ShareDialog
        isOpen={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        conversationId={conversationId}
        messageCount={messages.length}
      />
    </div>
  );
};

export default SkillBotEnhanced;
